import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Form, notification, Spin } from 'antd'
import { Drawer, DrawerFooter } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { MailTemplateForm } from '@/components/form/MailTemplateForm'
import type { MailTemplateFormValues } from '@/components/form/MailTemplateForm'
import { createMailTemplate, updateMailTemplate, getMailTemplateById, sendTestMailTemplate } from '@/api/mail-templates.api'
import { currentUserAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'

interface MailTemplateFormDrawerProps {
    isOpen: boolean
    mode: 'create' | 'update'
    templateId: string | null
    onClose: () => void
}

export function MailTemplateFormDrawer({
    isOpen,
    mode,
    templateId,
    onClose,
}: MailTemplateFormDrawerProps) {
    const [form] = Form.useForm<MailTemplateFormValues>()
    const queryClient = useQueryClient()

    const currentUser = useAtomValue(currentUserAtom)
    const showUnitColumn = currentUser?.userType === 1
    const [isTesting, setIsTesting] = useState(false)

    const { data: templateDetail, isLoading: isFetchingTemplate } = useQuery({
        queryKey: ['mail-template-detail', templateId],
        queryFn: ({ signal }) =>
            templateId ? getMailTemplateById(templateId, signal) : Promise.resolve(null),
        enabled: isOpen && mode === 'update' && !!templateId,
        refetchOnMount: 'always',
        staleTime: 0,
        gcTime: 0,
    })

    useEffect(() => {
        if (isOpen) {
            if (mode === 'update' && templateDetail) {
                form.setFieldsValue({
                    id: templateDetail.id,
                    unitId: templateDetail.unitId,
                    unitName: templateDetail.unitName,
                    name: templateDetail.name,
                    functionId: templateDetail.functionId,
                    title: templateDetail.title,
                    content: templateDetail.content || '',
                    attachedFile:
                        templateDetail.files && templateDetail.files.length > 0
                            ? templateDetail.files.map((file: any) => ({
                                uid: file.id,
                                name: file.originFileName,
                                status: 'done',
                                url: file.fileUrl,
                                id: file.id,
                            }))
                            : [],
                })
            } else if (mode === 'create') {
                form.resetFields()
            }
        } else {
            form.resetFields()
        }
    }, [isOpen, mode, templateDetail, form])

    const createMutation = useMutation({
        mutationFn: createMailTemplate,
        onSuccess: (response: any) => {
            notification.success({ message: response.responseMessage || 'Thêm mới mẫu mail thành công!' })
            queryClient.invalidateQueries({ queryKey: ['mail-templates'] })
            onClose()
        },
    })

    const updateMutation = useMutation({
        mutationFn: (values: any) => {
            if (!templateId) throw new Error('Missing template ID')
            return updateMailTemplate(templateId, values)
        },
        onSuccess: (response: any) => {
            notification.success({ message: response.responseMessage || 'Cập nhật mẫu mail thành công!' })
            queryClient.invalidateQueries({ queryKey: ['mail-templates'] })
            onClose()
        },
    })

    const isSubmitting = createMutation.isPending || updateMutation.isPending
    const isDrawerDisabled = isSubmitting || isFetchingTemplate || isTesting

    const extractFileIds = (files: any[]): string[] => {
        if (files.length === 0) return []
        return files.map(file => file.id || file.response?.id || null).filter(Boolean) as string[]
    }

    const handleTestMail = async () => {
        try {
            const values = await form.validateFields()
            setIsTesting(true)
            const fileIds = extractFileIds(values.attachedFile || [])

            const response = await sendTestMailTemplate({
                unitId: showUnitColumn ? values.unitId : currentUser?.unitId,
                name: values.name,
                functionId: values.functionId,
                title: values.title,
                content: values.content,
                fileIds: fileIds,
                receiveMail: values.receiveMail,
            })

            notification.success({ message: response.responseMessage || 'Gửi test mail thành công, vui lòng kiểm tra hộp thư của bạn.' })
        } catch (error: any) {
            if (error && error.errorFields) return
            notification.error({ message: error?.message || 'Gửi test mail thất bại.' })
        } finally {
            setIsTesting(false)
        }
    }

    const handleSubmit = (values: MailTemplateFormValues) => {
        const fileIds = extractFileIds(values.attachedFile || [])

        const payload: any = {
            unitId: showUnitColumn ? values.unitId : currentUser?.unitId,
            name: values.name.trim(),
            functionId: values.functionId,
            title: values.title.trim(),
            content: values.content,
            fileIds: fileIds,
            ...(mode === 'update' ? { status: templateDetail?.status ?? 1 } : {}),
        }

        if (mode === 'create') {
            createMutation.mutate(payload)
        } else {
            updateMutation.mutate(payload)
        }
    }

    return (
        <Drawer
            open={isOpen}
            onClose={onClose}
            title={mode === 'create' ? 'THÊM MỚI MẪU MAIL' : 'CẬP NHẬT MẪU MAIL'}
            styles={{ body: { overflow: 'auto' } }}
            footer={
                <DrawerFooter>
                    <Button
                        variant="outline"
                        size="md"
                        onClick={onClose}
                        disabled={isDrawerDisabled}
                        className="min-w-[120px] h-10 rounded-full"
                    >
                        Hủy
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() => form.submit()}
                        disabled={isDrawerDisabled}
                        className="min-w-[120px] h-10 rounded-full bg-[#CE7A58] hover:bg-[#b8694a] border-none"
                    >
                        {isSubmitting ? 'Đang lưu...' : 'Lưu'}
                    </Button>
                </DrawerFooter>
            }
        >
            <Spin spinning={isFetchingTemplate} description="Đang tải dữ liệu...">
                <div className="min-h-[300px] flex flex-col">
                    {(mode === 'create' || templateDetail) && (
                        <>
                            <MailTemplateForm
                                form={form}
                                mode={mode}
                                showUnitColumn={showUnitColumn}
                                onSubmit={handleSubmit}
                            />
                            <div className="mt-4">
                                <Button
                                    variant="outline"
                                    size="md"
                                    onClick={handleTestMail}
                                    disabled={isDrawerDisabled}
                                    className="min-w-[150px] h-10 border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10"
                                >
                                    {isTesting ? 'Đang gửi...' : 'Gửi kiểm tra'}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Spin>
        </Drawer>
    )
}